import { useState } from "react";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Moment } from "moment";
import { Button } from "../../../common";
import { CALENDAR_LAYERS } from "../../../../constants";
import { CalendarLayer } from "../../../../enums";

type CalendarToolbarProps = {
  weekStart: Moment;
  selectedDay?: Moment;
  layer: CalendarLayer;
  onLayerChange: (layer: CalendarLayer) => void;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onDaySelect: (day: Moment) => void;
  onWeekView: () => void;
};

export const CalendarToolbar = ({
  weekStart,
  selectedDay,
  layer,
  onLayerChange,
  onPrevious,
  onNext,
  onToday,
  onDaySelect,
  onWeekView,
}: CalendarToolbarProps) => {
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement>();
  const weekEnd = weekStart.clone().add(6, "days");
  const periodLabel = selectedDay ? "day" : "week";

  return (
    <Box
      className="dashboard-card"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        p: 2,
        mb: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton aria-label={`Previous ${periodLabel}`} onClick={onPrevious}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          sx={{
            minWidth: 170,
            textAlign: "center",
            fontSize: 15,
            fontWeight: 600,
            color: "var(--dashboard-text-primary)",
          }}
        >
          {selectedDay
            ? selectedDay.format("ddd, MMM D, YYYY")
            : `${weekStart.format("MMM D")} – ${weekEnd.format("MMM D, YYYY")}`}
        </Typography>
        <IconButton aria-label={`Next ${periodLabel}`} onClick={onNext}>
          <ChevronRightIcon />
        </IconButton>
        <IconButton
          aria-label="Select date"
          onClick={(e) => setDatePickerAnchor(e.currentTarget)}
        >
          <CalendarMonthOutlinedIcon />
        </IconButton>
        <Button
          label="Today"
          variantType="secondary"
          onClick={onToday}
          sx={{ height: 36, px: 2 }}
        />
        {selectedDay && (
          <Button
            label="Week"
            variantType="secondary"
            onClick={onWeekView}
            sx={{ height: 36, px: 2 }}
          />
        )}
        <Popover
          open={!!datePickerAnchor}
          anchorEl={datePickerAnchor}
          onClose={() => setDatePickerAnchor(undefined)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateCalendar
              value={selectedDay ?? weekStart}
              onChange={(value: Moment | null, selectionState) => {
                if (value && selectionState === "finish") {
                  onDaySelect(value);
                  setDatePickerAnchor(undefined);
                }
              }}
            />
          </LocalizationProvider>
        </Popover>
      </Box>

      <Box
        className="dashboard-segmented"
        role="tablist"
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        {CALENDAR_LAYERS.map((item) => (
          <Box
            key={item.value}
            role="tab"
            tabIndex={0}
            aria-selected={layer === item.value}
            className={`dashboard-segmented-item${layer === item.value ? " active" : ""}`}
            sx={{ flex: 1, px: 2 }}
            onClick={() => onLayerChange(item.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onLayerChange(item.value);
              }
            }}
          >
            {item.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
